import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, forkJoin, map, of, switchMap, tap, withLatestFrom, retryWhen, concatMap, throwError, timer } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';
import { GroupService } from './group.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WhatsAppService {
  private apiUrl = `api/whatsapp`;
  private conversationsSubject = new BehaviorSubject<any[]>([]);
  private messagesSubject = new BehaviorSubject<any[]>([]);

  conversations$ = this.conversationsSubject.asObservable();
  messages$ = this.messagesSubject.asObservable();

  private patientId?: string;
  groupId?: string;

  /**
   * Get current messages value
   */
  getCurrentMessages(): any[] {
    return this.messagesSubject.getValue();
  }

  /**
   * Get current conversations value
   */
  getCurrentConversations(): any[] {
    return this.conversationsSubject.getValue();
  }

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService,
    private groupService: GroupService
  ) {
    this.subscribeToCurrentPatient();
    this.subscribeToCurrentGroup();
    this.setupSocketListeners();
  }

  /**
   * Subscribe to current group changes (like patient service pattern)
   */
  private subscribeToCurrentGroup(): void {
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          console.log('[WHATSAPP SERVICE] Group selected', {
            groupId: this.groupId,
            groupName: selectedGroup.name
          });
          // Reload messages for current patient if we have one
          if (this.patientId) {
            return this.loadConversationData(this.patientId);
          }
          return EMPTY;
        } else {
          this.groupId = undefined;
          console.log('[WHATSAPP SERVICE] No group selected');
          return EMPTY;
        }
      }),
      tap((merged: any) => {
        if (merged) {
          this.messagesSubject.next(merged);
          this.groupMessagesIntoConversations(merged);
        }
      })
    ).subscribe();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientId = patient.id;
          // Clear data synchronously before async load starts
          this.messagesSubject.next([]);
          this.conversationsSubject.next([]);
          return this.loadConversationData(patient.id).pipe(
            retryWhen(errors =>
              errors.pipe(
                concatMap((error, index) => {
                  // Don't retry for client errors (4xx) except 408 (timeout) and 429 (rate limit)
                  const status = error?.status;
                  const shouldRetry = !status || (status >= 500) || status === 408 || status === 429;

                  if (!shouldRetry || index >= 2) {
                    // Max retries exceeded or non-retryable error
                    return throwError(() => error);
                  }

                  const delayTime = 1000 * Math.pow(2, index); // Exponential backoff: 1s, 2s
                  console.warn(`Retrying WhatsApp messages load (attempt ${index + 1}/3) after ${delayTime}ms`, error);
                  return timer(delayTime);
                })
              )
            ),
            catchError((error) => {
              console.error('Error loading WhatsApp messages after retries:', error);
              return of([]);
            })
          );
        } else {
          // If there's no current patient, clear the messages
          this.patientId = undefined;
          this.messagesSubject.next([]);
          this.conversationsSubject.next([]);
          return of([]);
        }
      }),
      tap((merged: any) => {
        const messages = Array.isArray(merged) ? merged : [];
        this.messagesSubject.next(messages);
        this.groupMessagesIntoConversations(messages);
      }),
      catchError((error) => {
        console.error('Error in WhatsApp messages subscription:', error);
        this.messagesSubject.next([]);
        this.conversationsSubject.next([]);
        return of([]);
      })
    ).subscribe();
  }

  /**
   * Load both delivered messages and pending rows for a patient and merge
   * them into a single ordered list. Pending rows carry `__kind: 'pending'`
   * so the dialog template can render them with state-specific UI.
   */
  private loadConversationData(patientId: string): Observable<any[]> {
    return forkJoin({
      messages: this.loadMessagesByPatient(patientId).pipe(map((r: any) => r?.data || [])),
      pending: this.loadPendingMessages({ patientId }).pipe(
        map((r: any) => r?.data || []),
        catchError(() => of([]))
      )
    }).pipe(
      map(({ messages, pending }) => this.sortMessages([...messages, ...pending]))
    );
  }

  /**
   * Sort a mixed list of WhatsAppMessage / pending rows by their effective
   * timestamp. WhatsAppMessage rows have a unix `timestamp` (seconds) when
   * delivered or `createdAt` if not yet delivered; pending rows always have
   * `createdAt` only.
   */
  private sortMessages(rows: any[]): any[] {
    return [...rows].sort((a: any, b: any) => {
      const timeA = a.timestamp ? new Date(a.timestamp * 1000).getTime() : new Date(a.createdAt).getTime();
      const timeB = b.timestamp ? new Date(b.timestamp * 1000).getTime() : new Date(b.createdAt).getTime();
      return timeA - timeB;
    });
  }

  /**
   * Identity key for an arbitrary row in the merged messages list.
   * WhatsAppMessage rows use `messageId`; pending rows use `id` (UUID).
   * The `__kind` discriminator keeps them separate even on UUID collisions.
   */
  private rowKey(row: any): string {
    if (row.__kind === 'pending') return `pending:${row.id}`;
    return `msg:${row.messageId || row.id || ''}`;
  }

  /**
   * Insert or replace a row in messagesSubject by identity key, then sort
   * and re-emit. Used by all socket listeners.
   */
  private upsertMessage(row: any): void {
    const current = this.messagesSubject.getValue() || [];
    const incomingKey = this.rowKey(row);
    const idx = current.findIndex((m: any) => this.rowKey(m) === incomingKey);
    if (idx < 0) {
      current.push(row);
    } else {
      current[idx] = row;
    }
    const sorted = this.sortMessages(current);
    this.messagesSubject.next(sorted);
    this.groupMessagesIntoConversations(sorted);
  }

  /**
   * Remove a row from messagesSubject by identity key.
   */
  private removeMessage(key: string): void {
    const current = this.messagesSubject.getValue() || [];
    const next = current.filter((m: any) => this.rowKey(m) !== key);
    if (next.length === current.length) return;
    this.messagesSubject.next(next);
    this.groupMessagesIntoConversations(next);
  }

  private setupSocketListeners(): void {
    // Helper that filters socket events to the currently-loaded patient.
    const forCurrentPatient = (eventName: string) =>
      this.socketService.listen(eventName, 'whatsapp-service').pipe(
        withLatestFrom(this.patientService.currentPatient$),
        map(([row, currentPatient]) => {
          const currentPatientId = currentPatient?.id;
          if (row && row.patientId === currentPatientId && currentPatientId) {
            return row;
          }
          return null;
        })
      );

    // Delivered/inbound message lifecycle
    forCurrentPatient('whatsappMessageReceived').subscribe(message => {
      if (message) this.upsertMessage(message);
    });
    forCurrentPatient('whatsappMessageSent').subscribe(message => {
      if (!message) return;
      // When this confirmed message originated from a queued pending row
      // (server attached the linkage before emitting), drop the pending
      // placeholder in-band so it doesn't linger alongside the real message.
      // Independent of the separate whatsappPendingDeleted event, which can
      // race or be missed under flaky network conditions.
      if (message.pendingMessageId) {
        this.removeMessage(`pending:${message.pendingMessageId}`);
      }
      this.upsertMessage(message);
    });
    forCurrentPatient('whatsappMessageStatusUpdated').subscribe(message => {
      if (message) this.upsertMessage(message);
    });

    // Pending message lifecycle (queued behind consent template).
    // Backend always emits the full client-shaped pending row, so we can
    // upsert directly. Created and StatusUpdated share handling because
    // the only difference is whether the row is new or existing.
    forCurrentPatient('whatsappPendingCreated').subscribe(pending => {
      if (pending) this.upsertMessage(pending);
    });
    forCurrentPatient('whatsappPendingStatusUpdated').subscribe(pending => {
      if (pending) this.upsertMessage(pending);
    });
    forCurrentPatient('whatsappPendingDeleted').subscribe(pending => {
      if (pending) this.removeMessage(this.rowKey(pending));
    });
  }

  /**
   * Load WhatsApp messages for a patient (following patient notes pattern)
   */
  loadMessagesByPatient(patientId: string, limit: number = 50, offset: number = 0): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/messages/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { limit: limit.toString(), offset: offset.toString() }
    }).pipe(
      catchError(error => {
        console.error('Error loading WhatsApp messages:', error);
        return of({ success: false, data: [], count: 0 });
      })
    );
  }

  /**
   * Load conversation with a specific phone number
   */
  loadConversation(phoneNumber: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/conversation/${encodeURIComponent(phoneNumber)}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.messagesSubject.next(response.data);
        }
      }),
      catchError(error => {
        console.error('Error loading conversation:', error);
        return of({ success: false, data: [] });
      })
    );
  }

  /**
   * Send a text message via WhatsApp
   */
  sendTextMessage(to: string, message: string, options?: {
    patientId?: string;
    patientCommunicationId?: string;
    autoSendIntroTemplate?: boolean;
    introTemplateName?: string;
    doctorName?: string;
  }): Observable<any> {
    const token = localStorage.getItem('token');
    // Include groupId from service (like patient service pattern)
    const body: any = { 
      to, 
      message, 
      ...options,
      groupId: this.groupId // Use service groupId (GroupInterceptor will also add it, but we want it explicitly)
    };
    
    console.log('[WHATSAPP SERVICE] Sending text message with groupId', {
      to,
      patientId: options?.patientId,
      groupId: body.groupId,
      hasGroupId: !!body.groupId
    });
    
    return this.http.post<any>(`${this.apiUrl}/send-text`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(error => {
        console.error('[WHATSAPP SERVICE] Error sending WhatsApp message:', error);
        return of({ success: false, error: error.error?.error || 'Failed to send message' });
      })
    );
  }

  /**
   * Send a templated message via WhatsApp
   */
  sendTemplatedMessage(to: string, templateName: string, languageCode: string = 'en_ZA', parameters: any[] = [], options?: {
    patientId?: string;
    patientCommunicationId?: string;
  }): Observable<any> {
    const token = localStorage.getItem('token');
    const body: any = { to, templateName, languageCode, parameters, ...options };
    return this.http.post<any>(`${this.apiUrl}/send-template`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(error => {
        console.error('Error sending templated message:', error);
        return of({ success: false, error: error.error?.error || 'Failed to send message' });
      })
    );
  }

  /**
   * Send a voice note via WhatsApp (base64)
   */
  sendVoiceNote(to: string, audioBase64: string, mimeType: string = 'audio/webm', options?: {
    patientId?: string;
    patientCommunicationId?: string;
  }): Observable<any> {
    const token = localStorage.getItem('token');
    const body: any = { 
      to, 
      audioBase64, 
      mimeType,
      groupId: this.groupId,
      ...options
    };
    
    console.log('[WHATSAPP SERVICE] Sending voice note with groupId', {
      to,
      mimeType,
      patientId: options?.patientId,
      groupId: body.groupId,
      hasGroupId: !!body.groupId
    });
    
    return this.http.post<any>(`${this.apiUrl}/send-voice-base64`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(error => {
        console.error('[WHATSAPP SERVICE] Error sending voice note:', error);
        return of({ success: false, error: error.error?.error || 'Failed to send voice note' });
      })
    );
  }

  /**
   * Send media (image, document, or video) via WhatsApp
   */
  sendMedia(to: string, mediaUrl: string, mimeType: string, filename: string, mediaType: 'image' | 'document' | 'video', caption?: string, options?: {
    patientId?: string;
    patientCommunicationId?: string;
  }): Observable<any> {
    const token = localStorage.getItem('token');
    const body: any = { 
      to, 
      mediaUrl, 
      mimeType,
      filename,
      mediaType,
      caption,
      groupId: this.groupId,
      ...options
    };
    
    console.log('[WHATSAPP SERVICE] Sending media with groupId', {
      to,
      mimeType,
      mediaType,
      filename,
      patientId: options?.patientId,
      groupId: body.groupId,
      hasGroupId: !!body.groupId
    });
    
    return this.http.post<any>(`${this.apiUrl}/send-media`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(error => {
        console.error('[WHATSAPP SERVICE] Error sending media:', error);
        return of({ success: false, error: error.error?.error || 'Failed to send media' });
      })
    );
  }

  /**
   * List pending WhatsApp messages (queued behind the consent-template handshake)
   * for a patient or phone number, scoped to the user's groups.
   */
  loadPendingMessages(filter: { patientId?: string; phoneNumber?: string; groupId?: string } = {}): Observable<any> {
    const token = localStorage.getItem('token');
    const params: any = {};
    if (filter.patientId) params.patientId = filter.patientId;
    if (filter.phoneNumber) params.phoneNumber = filter.phoneNumber;
    if (filter.groupId) params.groupId = filter.groupId;
    return this.http.get<any>(`${this.apiUrl}/pending`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    }).pipe(
      catchError(error => {
        console.error('[WHATSAPP SERVICE] Error loading pending messages:', error);
        return of({ success: false, data: [] });
      })
    );
  }

  /**
   * Retry a pending WhatsApp message by re-sending the consent template.
   * Backend rotates `templateMessageId` and resets `expiresAt`; the original
   * payload (text / voice blob / media blob) stays in place.
   */
  retryPendingMessage(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/pending/${id}/retry`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(error => {
        console.error('[WHATSAPP SERVICE] Error retrying pending message:', error);
        return of({ success: false, error: error.error?.error || 'Failed to retry pending message' });
      })
    );
  }

  /**
   * Cancel a pending WhatsApp message. Hard-deletes the row and the
   * associated blob server-side; the dialog can also remove it locally.
   */
  cancelPendingMessage(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete<any>(`${this.apiUrl}/pending/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(error => {
        console.error('[WHATSAPP SERVICE] Error cancelling pending message:', error);
        return of({ success: false, error: error.error?.error || 'Failed to cancel pending message' });
      })
    );
  }

  /**
   * Optimistically push a newly-queued pending row into the local message
   * list. Called by the chat dialog right after a send call returns
   * `pending_button_response` so the user sees the message stick in the
   * timeline immediately, without waiting for the socket round-trip.
   *
   * Insert-only: if the socket-driven `whatsappPendingCreated` (which carries
   * the authoritative payload, including `mediaUrl`) has already arrived,
   * skip — overwriting it with this stripped optimistic stub would clear
   * fields like `mediaUrl` and break audio/media playback until reload.
   */
  pushOptimisticPending(row: any): void {
    if (!row || !row.id) return;
    if (this.hasRow(`pending:${row.id}`)) return;
    const enriched = { __kind: 'pending', direction: 'outbound', status: 'pending', ...row };
    this.upsertMessage(enriched);
  }

  /**
   * Optimistically push a directly-delivered (within-24h-window) message
   * into the local list. Closes the flaky-socket failure mode where the
   * HTTP send succeeds, the message reaches the recipient, but the
   * `whatsappMessageSent` event never arrives so the dialog never updates.
   *
   * Insert-only: if the socket event has already populated the row with the
   * authoritative payload (mediaUrl from `persistMediaToBlobStorage`, etc.),
   * skip — overwriting it with this stub would strip those fields.
   */
  pushOptimisticDelivered(row: any): void {
    if (!row || !row.messageId) return;
    if (this.hasRow(`msg:${row.messageId}`)) return;
    const now = new Date();
    const enriched = {
      direction: 'outbound',
      status: 'sent',
      timestamp: Math.floor(now.getTime() / 1000),
      createdAt: now.toISOString(),
      ...row,
    };
    this.upsertMessage(enriched);
  }

  private hasRow(key: string): boolean {
    const current = this.messagesSubject.getValue() || [];
    return current.some((m: any) => this.rowKey(m) === key);
  }

  /**
   * Get message status
   */
  getMessageStatus(messageId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/message-status/${messageId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * Check WhatsApp configuration
   */
  checkConfig(): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/config`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * Group messages into conversations by phone number
   * Also adds a "New Conversation" entry for the current patient mobile number if it's different from existing conversations
   */
  private groupMessagesIntoConversations(messages: any[]): void {
    const conversationsMap = new Map<string, any>();

    // Sort messages by timestamp first (most recent first for initial load)
    const sortedMessages = [...messages].sort((a: any, b: any) => {
      const timeA = a.timestamp ? new Date(a.timestamp * 1000).getTime() : new Date(a.createdAt).getTime();
      const timeB = b.timestamp ? new Date(b.timestamp * 1000).getTime() : new Date(b.createdAt).getTime();
      return timeB - timeA; // Most recent first
    });

    sortedMessages.forEach(message => {
      const phoneNumber = message.direction === 'inbound' ? message.from : message.to;
      if (!phoneNumber) return;

      if (!conversationsMap.has(phoneNumber)) {
        conversationsMap.set(phoneNumber, {
          phoneNumber,
          messages: [],
          lastMessageAt: null,
          unreadCount: 0,
          hasPendingMessage: false,
          lastMessage: null,
          isCurrentNumber: false, // Will be set later if this matches patient.mobileNumber
          isHistorical: false // Will be set later if this doesn't match patient.mobileNumber
        });
      }

      const conversation = conversationsMap.get(phoneNumber);
      conversation.messages.push(message);

      // Update last message timestamp
      const messageTime = message.timestamp ? new Date(message.timestamp * 1000) : new Date(message.createdAt);
      if (!conversation.lastMessageAt || messageTime > conversation.lastMessageAt) {
        conversation.lastMessageAt = messageTime;
        conversation.lastMessage = message;
      }

      // Count unread inbound messages
      if (message.direction === 'inbound' && message.status !== 'read') {
        conversation.unreadCount++;
      }

      // Check for pending messages (waiting for button response).
      // Includes legacy pending_button_response on WhatsAppMessage rows,
      // sent template messages, and the new __kind='pending' rows whose
      // status is anything but 'completed' (which never appears here
      // because completed rows are deleted server-side).
      if (
        message.__kind === 'pending'
        || message.status === 'pending_button_response'
        || (message.status === 'sent' && message.messageType === 'template')
      ) {
        conversation.hasPendingMessage = true;
      }
    });

    // Normalize phone numbers for comparison (remove + and spaces)
    const normalizePhoneNumber = (phone: string): string => {
      if (!phone) return '';
      return phone.replace(/[\s+]/g, '').trim();
    };

    // Check if current patient mobile number exists in conversations
    // Use the patient getter (like patient notes pattern)
    const currentPatient = this.patientService.patient;
    const currentPatientMobileNumber = currentPatient?.mobileNumber;
    const normalizedCurrentNumber = normalizePhoneNumber(currentPatientMobileNumber || '');
    
    // Mark conversations as current or historical based on patient's current mobile number
    let hasCurrentNumberConversation = false;
    conversationsMap.forEach((conversation) => {
      const normalizedConversationNumber = normalizePhoneNumber(conversation.phoneNumber);
      
      if (normalizedCurrentNumber && normalizedConversationNumber === normalizedCurrentNumber) {
        conversation.isCurrentNumber = true;
        conversation.isHistorical = false;
        hasCurrentNumberConversation = true;
      } else if (normalizedCurrentNumber && normalizedConversationNumber !== normalizedCurrentNumber) {
        conversation.isCurrentNumber = false;
        conversation.isHistorical = true; // This is an old number
      } else {
        // No current patient number to compare against
        conversation.isCurrentNumber = false;
        conversation.isHistorical = false;
      }
    });

    // If patient has a current mobile number but no conversation exists for it, add a "New Conversation" entry
    if (normalizedCurrentNumber && !hasCurrentNumberConversation) {
      conversationsMap.set(currentPatientMobileNumber, {
        phoneNumber: currentPatientMobileNumber,
        messages: [],
        lastMessageAt: null,
        unreadCount: 0,
        hasPendingMessage: false,
        lastMessage: null,
        isCurrentNumber: true,
        isHistorical: false,
        isEmpty: true // Mark as empty so UI can show "New conversation" or similar
      });
    }

    // Convert map to array
    const conversationsArray = Array.from(conversationsMap.values());
    
    // Sort messages within each conversation chronologically first
    conversationsArray.forEach(conversation => {
      conversation.messages.sort((a: any, b: any) => {
        const timeA = a.timestamp ? new Date(a.timestamp * 1000).getTime() : new Date(a.createdAt).getTime();
        const timeB = b.timestamp ? new Date(b.timestamp * 1000).getTime() : new Date(b.createdAt).getTime();
        return timeA - timeB; // Oldest first
      });
    });
    
    // Sort conversations: current number first, then by most recent message
    conversationsArray.sort((a: any, b: any) => {
      // Current number always comes first
      if (a.isCurrentNumber && !b.isCurrentNumber) return -1;
      if (!a.isCurrentNumber && b.isCurrentNumber) return 1;
      
      // Then sort by most recent message
      const timeA = a.lastMessageAt ? a.lastMessageAt.getTime() : 0;
      const timeB = b.lastMessageAt ? b.lastMessageAt.getTime() : 0;
      return timeB - timeA; // Most recent first
    });
    
    const conversations = conversationsArray;

    this.conversationsSubject.next(conversations);
  }

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    // Remove + and format: +27821234567 -> 082 123 4567
    const cleaned = phoneNumber.replace(/[^\d]/g, '');
    if (cleaned.startsWith('27')) {
      const local = cleaned.substring(2);
      if (local.length === 9) {
        return `0${local.substring(0, 2)} ${local.substring(2, 5)} ${local.substring(5)}`;
      }
    }
    return phoneNumber;
  }

  /**
   * Mask phone number for privacy
   */
  maskPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    const formatted = this.formatPhoneNumber(phoneNumber);
    return formatted.slice(0, -4) + '****';
  }
}

